/**
 * Product Images JS
 * 
 * Handles product image zoom and swapping for product pages.
 * 
 * Features:
 * - Click to zoom on main product image.
 * - Mouse move to pan zoomed image.
 * - Click again to reset zoom.
 * - (Extendable) Handles image swapping for product variants.
 * 
 * Usage:
 * This script is loaded on product pages. It expects product images to be
 * wrapped in elements with the class `.product-image-main`.
 * 
 * Functions:
 * - initProductZoom(): Initializes zoom functionality on main product images.
 * 
 * To extend:
 * - Add variant image swapping logic as needed.
 * 
 * Example HTML structure:
 * <div class="product-image-main">
 *   <img src="..." alt="Product image">
 * </div>
 * 
 * See theme documentation for more details.
 */

// Global storage for cleanup controllers to prevent memory leaks
let productImageControllers = new WeakMap();
let thumbScrollControllers = new WeakMap();

function promoteSlideImage(slideOrIndex) {
  var slide = slideOrIndex;
  if (typeof slideOrIndex === "number") {
    slide = document.querySelector('.product-main-slide[data-index="' + slideOrIndex + '"]');
  }
  if (!slide) return;
  var img = slide.querySelector('img[data-full-srcset], img[data-full-src]');
  if (!img || img.dataset.fullApplied === "true") return;

  var picture = img.closest("picture");
  var source = picture ? picture.querySelector("source[type='image/webp']") : null;

  if (img.dataset.fullSrcset) img.srcset = img.dataset.fullSrcset;
  if (img.dataset.fullSrc) img.src = img.dataset.fullSrc;
  if (img.dataset.fullSizes) img.sizes = img.dataset.fullSizes;
  if (source && img.dataset.fullSrcsetWebp) source.srcset = img.dataset.fullSrcsetWebp;
  if (source && img.dataset.fullSizes) source.sizes = img.dataset.fullSizes;
  img.dataset.fullApplied = "true";
  img.loading = "eager";
  img.fetchPriority = "high";
}

function initProductThumbScrolling() {
  var thumbs = document.querySelectorAll(".product__thumb");
  var slides = document.querySelectorAll(".product-main-slide");
  var slideshow = document.querySelector(".product-slideshow");

  if (!thumbs.length || !slides.length) return;

  // Check if Flickity is handling the slideshow
  // If so, don't interfere with native scroll behavior
  if (slideshow && slideshow.classList.contains("flickity-enabled")) {
    return;
  }

  thumbs.forEach(function (thumb, i) {
    // Clean up existing controllers if they exist
    const existingController = thumbScrollControllers.get(thumb);
    if (existingController) {
      existingController.abort();
    }

    // Create new AbortController for proper cleanup
    const controller = new AbortController();
    thumbScrollControllers.set(thumb, controller);

    thumb.addEventListener("click", function (e) {
      e.preventDefault();

      // Double-check Flickity isn't enabled before proceeding
      if (slideshow && slideshow.classList.contains("flickity-enabled")) {
        return;
      }

      // Defensive checks
      if (!thumbs || !slides) return;

      thumbs.forEach(function (el) {
        if (el && el.classList) {
          el.classList.remove("is-active");
        }
      });
      
      if (thumb && thumb.classList) {
        thumb.classList.add("is-active");
      }

      var index = parseInt(thumb.getAttribute("data-index"), 10);
      promoteSlideImage(index);
      if (slides[index] && slides[index].scrollIntoView) {
        slides[index].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }, { signal: controller.signal });
  });
}

// Cleanup function for when sections are unloaded
function cleanupProductImages() {
  // Clean up zoom controllers
  productImageControllers = new WeakMap();
  
  // Clean up thumb scroll controllers
  thumbScrollControllers = new WeakMap();
}

// Analytics tracking for carousel interactions
function trackCarouselInteractions() {
  const carousel = document.querySelector('.product-slideshow');
  if (!carousel) return;

  // Track slide changes
  if (typeof gtag !== 'undefined') {
    carousel.addEventListener('change', function(event) {
      gtag('event', 'carousel_slide_change', {
        'event_category': 'Product Images',
        'event_label': 'Slide ' + (event.detail.index + 1),
        'value': event.detail.index + 1
      });
    });

    // Track zoom interactions
    carousel.addEventListener('click', function(event) {
      if (event.target.closest('.product__photo-zoom')) {
        gtag('event', 'image_zoom', {
          'event_category': 'Product Images',
          'event_label': 'Zoom Interaction'
        });
      }
    });
  }
}

// Initialize optimizations
function initProductImageOptimizations() {
  promoteSlideImage(document.querySelector(".product-main-slide.starting-slide") || 0);
  document.querySelectorAll(".product-main-slide").forEach(function (slide) {
    slide.addEventListener("click", function () {
      promoteSlideImage(slide);
    }, { passive: true });
  });

  var slideshow = document.querySelector(".product-slideshow");
  if (slideshow) {
    slideshow.addEventListener("change", function (event) {
      if (event.detail && typeof event.detail.index === "number") {
        promoteSlideImage(event.detail.index);
      }
    });
    slideshow.addEventListener("select.flickity", function (event) {
      var selected = slideshow.querySelector(".is-selected");
      promoteSlideImage(selected);
    });
  }

  trackCarouselInteractions();
}

// Run when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductImageOptimizations);
} else {
  initProductImageOptimizations();
}

// Initialise on page load
document.addEventListener("DOMContentLoaded", function () {
  initProductThumbScrolling();
});

// Re-initialise after Shopify section is loaded (for swatch navigation, AJAX, etc.)
document.addEventListener("shopify:section:load", function (e) {
  // Clean up previous instances to prevent memory leaks
  cleanupProductImages();
  
  // Re-initialize with fresh controllers
  initProductThumbScrolling();
});

// Clean up when section is unloaded (optional, for when theme supports it)
document.addEventListener("shopify:section:unload", function (e) {
  cleanupProductImages();
});
